<template>
  <div class="student-list">
    <header class="page-header">
      <div>
        <h2>学员清单</h2>
        <p class="subtitle">查看学员档案，管理推荐名额与黑名单状态</p>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="姓名 / 手机号 / 身份证" />
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadStudents">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
        <button class="success-button" @click="exportToExcel" :disabled="loading || pagination.total === 0">
          {{ exporting ? '导出中...' : '导出Excel' }}
        </button>
      </div>
    </section>

    <section class="table-wrapper" v-if="students.length">
      <table>
        <thead>
          <tr>
            <th>姓名</th>
            <th>手机号</th>
            <th>身份证</th>
            <th>性别</th>
            <th>年龄</th>
            <th>可用推荐名额</th>
            <th>黑名单状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="student in students" :key="student.id">
            <td>{{ student.name }}</td>
            <td>
              {{ getDisplayValue(student.id, 'phone', student.phone) }}
              <button
                class="decrypt-btn"
                @click="toggleDecrypt(student.id, 'phone')"
                :title="decryptedValues['STUDENT_PROFILE_' + student.id + '_phone'] ? '隐藏' : '查看'"
              >
                <svg v-if="decryptedValues['STUDENT_PROFILE_' + student.id + '_phone']" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </td>
            <td>
              {{ getDisplayValue(student.id, 'idNumber', student.idNumber) || '—' }}
              <button
                v-if="student.idNumber"
                class="decrypt-btn"
                @click="toggleDecrypt(student.id, 'idNumber')"
                :title="decryptedValues['STUDENT_PROFILE_' + student.id + '_idNumber'] ? '隐藏' : '查看'"
              >
                <svg v-if="decryptedValues['STUDENT_PROFILE_' + student.id + '_idNumber']" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </td>
            <td>{{ student.sex || '—' }}</td>
            <td>{{ student.age !== null && student.age !== undefined ? student.age : '—' }}</td>
            <td>
              <span class="quota-link" @click="openQuotaDetailModal(student)">{{ formatQuota(student.availableReferralQuota) }}</span>
            </td>
            <td>
              <span :class="['status-tag', student.activeBlacklistCount > 0 ? 'danger' : 'normal']">
                {{ student.activeBlacklistCount > 0 ? '已加入' : '正常' }}
              </span>
            </td>
            <td>
              <button class="link-button" @click="openProfile(student.id)">详情</button>
              <button
                v-if="student.activeBlacklistCount === 0"
                class="danger-link"
                @click="openBlacklistModal(student)"
              >
                加入黑名单
              </button>
              <button
                v-else
                class="ghost-button small"
                @click="removeBlacklist(student)"
              >
                解除黑名单
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">
          下一页
        </button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无学员档案</p>
    </div>

    <div v-if="showBlacklistModal" class="modal-backdrop">
      <div class="modal">
        <h3>加入黑名单</h3>
        <p class="modal-tip">学员：{{ blacklistTarget?.name }}（{{ blacklistTarget?.phone }}）</p>
        <textarea v-model="blacklistReason" rows="4" placeholder="请填写加入黑名单的原因" />
        <div class="modal-actions">
          <button class="ghost-button" @click="closeBlacklistModal">取消</button>
          <button class="primary-button" :disabled="loading || !blacklistReason.trim()" @click="submitBlacklist">
            确认
          </button>
        </div>
      </div>
    </div>

    <!-- 解除黑名单模态框 -->
    <div v-if="showRemoveBlacklistModal" class="modal-backdrop" @click="closeRemoveBlacklistModal">
      <div class="modal" @click.stop>
        <h3>解除黑名单</h3>
        <div class="form-summary">
          <p><strong>学员：</strong>{{ removeBlacklistTarget?.name }}（{{ removeBlacklistTarget?.phone }}）</p>
          <p v-if="removeBlacklistRecord?.reason"><strong>加入原因：</strong>{{ removeBlacklistRecord.reason }}</p>
        </div>
        <form @submit.prevent="submitRemoveBlacklist">
          <div class="form-group">
            <label>
              <span>解除备注</span>
              <textarea
                rows="3"
                v-model="removeBlacklistRemark"
                placeholder="可选，填写解除黑名单的备注说明"
              ></textarea>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="ghost-button" @click="closeRemoveBlacklistModal">取消</button>
            <button type="submit" class="primary-button" :disabled="loading">
              {{ loading ? '处理中...' : '确认解除' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showQuotaDetailModal" class="modal-backdrop" @click="closeQuotaDetailModal">
      <div class="modal quota-detail-modal" @click.stop>
        <div class="modal-header">
          <h3>推荐名额详情</h3>
          <button class="close-button" @click="closeQuotaDetailModal">×</button>
        </div>
        <div class="modal-body">
          <p class="modal-tip">学员：{{ quotaDetailTarget?.name }}（{{ quotaDetailTarget?.phone }}）</p>
          <div v-if="loadingQuotaRecords" class="loading-state">加载中...</div>
          <div v-else-if="quotaRecords.length === 0" class="empty-state-small">
            <p>暂无推荐名额记录</p>
          </div>
          <div v-else class="quota-records-list">
            <div v-for="record in quotaRecords" :key="record.id" class="quota-record-card">
              <div class="record-header">
                <span class="record-date">{{ formatDate(record.createdAt) }}</span>
                <span :class="['record-status', record.used ? 'used' : 'available']">
                  {{ record.used ? '已使用' : '可用' }}
                </span>
              </div>
              <div class="record-content">
                <p><strong>推荐学员：</strong>{{ record.recommendedStudentName || '—' }}（{{ record.recommendedStudentPhone || '—' }}）</p>
                <p><strong>课程名称：</strong>{{ record.courseTitle || '—' }}</p>
                <p><strong>名额值：</strong>{{ formatQuota(record.quotaValue) }}</p>
                <p><strong>有效期：</strong>{{ formatDateOnly(record.startTime) }} 至 {{ formatDateOnly(record.endTime) }}</p>
                <p v-if="record.used && record.usedAt"><strong>使用时间：</strong>{{ formatDate(record.usedAt) }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="ghost-button" @click="closeQuotaDetailModal">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="profile" class="drawer">
      <div class="drawer-header">
        <h3>学员详情</h3>
        <button class="close-button" @click="profile = null">×</button>
      </div>
      <div class="drawer-body">
        <section>
          <h4>基本信息</h4>
          <p><strong>姓名：</strong>{{ profile.name }}</p>
          <p>
            <strong>手机号：</strong>{{ getDisplayValue(profile.id, 'phone', profile.phone) }}
            <button
              class="decrypt-btn"
              @click="toggleDecrypt(profile.id, 'phone')"
              :title="decryptedValues['STUDENT_PROFILE_' + profile.id + '_phone'] ? '隐藏' : '查看'"
            >
              <svg v-if="decryptedValues['STUDENT_PROFILE_' + profile.id + '_phone']" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </p>
          <p v-if="profile.idNumber">
            <strong>身份证：</strong>{{ getDisplayValue(profile.id, 'idNumber', profile.idNumber) }}
            <button
              class="decrypt-btn"
              @click="toggleDecrypt(profile.id, 'idNumber')"
              :title="decryptedValues['STUDENT_PROFILE_' + profile.id + '_idNumber'] ? '隐藏' : '查看'"
            >
              <svg v-if="decryptedValues['STUDENT_PROFILE_' + profile.id + '_idNumber']" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </p>
          <p><strong>可用推荐名额：</strong>{{ formatQuota(profile.availableReferralQuota) }}</p>
          <p><strong>创建时间：</strong>{{ formatDate(profile.createdAt) }}</p>
          <p><strong>黑名单次数：</strong>{{ profile.activeBlacklistCount }}</p>
        </section>
        <section>
          <h4>微信账号</h4>
          <div class="wechat-info">
            <p><strong>微信昵称：</strong>{{ profile.wechatNickname || '—' }}</p>
            <p><strong>openid：</strong>{{ profile.wechatOpenid || '—' }}</p>
            <button
              v-if="profile.wechatOpenid"
              class="danger-button"
              @click="unbindWechat"
            >
              解绑微信账号
            </button>
          </div>
        </section>
        <section>
          <h4>黑名单记录</h4>
          <div v-if="blacklistHistory.length">
            <div v-for="record in blacklistHistory" :key="record.id" class="history-card">
              <p><strong>原因：</strong>{{ record.reason }}</p>
              <p><strong>状态：</strong>{{ record.removed ? '已解除' : '生效中' }}</p>
              <p><strong>创建时间：</strong>{{ formatDate(record.createdAt) }}</p>
              <p v-if="record.removedAt"><strong>解除时间：</strong>{{ formatDate(record.removedAt) }}</p>
              <p v-if="record.operatorName"><strong>操作人：</strong>{{ record.operatorName }}</p>
            </div>
          </div>
          <p v-else class="muted">暂无黑名单记录</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { studentService } from '@/services/studentService'
import { blacklistService } from '@/services/blacklistService'
import { sensitiveDataService } from '../services/sensitiveDataService'
import * as XLSX from 'xlsx'

export default {
  name: 'StudentList',
  data() {
    return {
      students: [],
      loading: false,
      filters: {
        keyword: ''
      },
      pagination: {
        page: 0,
        size: 10,
        total: 0
      },
      showBlacklistModal: false,
      blacklistTarget: null,
      blacklistReason: '',
      showRemoveBlacklistModal: false,
      removeBlacklistTarget: null,
      removeBlacklistRecord: null,
      removeBlacklistRemark: '',
      profile: null,
      blacklistHistory: [],
      showQuotaDetailModal: false,
      quotaDetailTarget: null,
      quotaRecords: [],
      loadingQuotaRecords: false,
      exporting: false,
      decryptedValues: {}
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    }
  },
  created() {
    this.loadStudents()
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      return value.replace('T', ' ').replace('Z', '')
    },
    formatDateOnly(value) {
      if (!value) return '—'
      return value.replace('T', ' ').split(' ')[0]
    },
    formatQuota(value) {
      if (value === null || value === undefined) return '0'
      return Number(value).toFixed(2)
    },
    async loadStudents() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          keyword: this.filters.keyword || undefined
        }
        const response = await studentService.fetchStudents(params)
        if (response.success) {
          this.students = response.data.content || []
          this.pagination.total = response.data.totalElements || 0
        }
      } catch (error) {
        console.error('加载学员列表失败', error)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters.keyword = ''
      this.pagination.page = 0
      this.loadStudents()
    },
    changePage(page) {
      if (page < 0) return
      if (page >= this.totalPages) return
      this.pagination.page = page
      this.loadStudents()
    },
    openBlacklistModal(student) {
      this.blacklistTarget = student
      this.blacklistReason = ''
      this.showBlacklistModal = true
    },
    closeBlacklistModal() {
      this.showBlacklistModal = false
      this.blacklistTarget = null
      this.blacklistReason = ''
    },
    async submitBlacklist() {
      if (!this.blacklistTarget) return
      try {
        this.loading = true
        const response = await blacklistService.addToBlacklist({
          studentId: this.blacklistTarget.id,
          reason: this.blacklistReason
        })
        if (response.success) {
          alert('已成功加入黑名单')
          this.closeBlacklistModal()
          this.loadStudents()
        }
      } catch (error) {
        console.error('加入黑名单失败', error)
        const errorMsg = error?.error || error?.message || '加入黑名单失败，请重试'
        alert(errorMsg)
      } finally {
        this.loading = false
      }
    },
    async removeBlacklist(student) {
      try {
        const record = await this.findActiveBlacklistRecord(student.id)
        if (!record) {
          alert('未找到黑名单记录')
          return
        }
        this.removeBlacklistTarget = student
        this.removeBlacklistRecord = record
        this.removeBlacklistRemark = ''
        this.showRemoveBlacklistModal = true
      } catch (error) {
        console.error('获取黑名单记录失败', error)
        alert('获取黑名单记录失败，请重试')
      }
    },
    closeRemoveBlacklistModal() {
      this.showRemoveBlacklistModal = false
      this.removeBlacklistTarget = null
      this.removeBlacklistRecord = null
      this.removeBlacklistRemark = ''
    },
    async submitRemoveBlacklist() {
      if (!this.removeBlacklistRecord) return
      try {
        this.loading = true
        const response = await blacklistService.removeFromBlacklist({
          recordId: this.removeBlacklistRecord.id,
          remark: this.removeBlacklistRemark || ''
        })
        if (response.success) {
          alert('解除黑名单成功')
          this.closeRemoveBlacklistModal()
          this.loadStudents()
          if (this.profile && this.removeBlacklistTarget && this.profile.id === this.removeBlacklistTarget.id) {
            this.loadBlacklistHistory(this.removeBlacklistTarget.id)
          }
        }
      } catch (error) {
        console.error('解除黑名单失败', error)
        const errorMsg = error?.error || error?.message || '解除黑名单失败，请重试'
        alert(errorMsg)
      } finally {
        this.loading = false
      }
    },
    async openProfile(studentId) {
      try {
        const response = await studentService.fetchStudentProfile(studentId)
        if (response.success) {
          this.profile = response.data
          await this.loadBlacklistHistory(studentId)
        }
      } catch (error) {
        console.error('获取学员档案失败', error)
      }
    },
    async loadBlacklistHistory(studentId) {
      try {
        const response = await blacklistService.fetchBlacklist({ page: 0, size: 100 })
        if (response.success) {
          this.blacklistHistory = (response.data.content || []).filter(
            (record) => record.studentId === studentId
          )
        }
      } catch (error) {
        console.error('加载黑名单记录失败', error)
      }
    },
    async findActiveBlacklistRecord(studentId) {
      const response = await blacklistService.fetchBlacklist({ page: 0, size: 100 })
      if (response.success) {
        const records = response.data.content || []
        return records.find((record) => record.studentId === studentId && !record.removed)
      }
      return null
    },
    async openQuotaDetailModal(student) {
      this.quotaDetailTarget = student
      this.showQuotaDetailModal = true
      this.loadingQuotaRecords = true
      this.quotaRecords = []
      
      try {
        const response = await studentService.fetchQuotaRecords(student.id)
        if (response.success) {
          this.quotaRecords = response.data || []
        }
      } catch (error) {
        console.error('获取推荐名额记录失败', error)
      } finally {
        this.loadingQuotaRecords = false
      }
    },
    closeQuotaDetailModal() {
      this.showQuotaDetailModal = false
      this.quotaDetailTarget = null
      this.quotaRecords = []
    },
    async exportToExcel() {
      try {
        this.exporting = true
        
        // 获取所有筛选后的学员数据
        const params = {
          keyword: this.filters.keyword || undefined
        }
        const response = await studentService.fetchAllStudents(params)
        
        if (!response.success || !response.data || !response.data.content) {
          alert('获取学员数据失败，请重试')
          return
        }

        const allStudents = response.data.content || []
        
        if (allStudents.length === 0) {
          alert('没有可导出的数据')
          return
        }

        // 准备Excel数据
        const excelData = allStudents.map(student => ({
          '姓名': student.name || '',
          '手机号': student.phone || '',
          '身份证号': student.idNumber || '',
          '性别': student.sex || '',
          '年龄': student.age !== null && student.age !== undefined ? student.age : '',
          '可用推荐名额': this.formatQuota(student.availableReferralQuota),
          '黑名单状态': student.activeBlacklistCount > 0 ? '已加入' : '正常',
          '创建时间': this.formatDate(student.createdAt)
        }))

        // 创建工作簿
        const worksheet = XLSX.utils.json_to_sheet(excelData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, '学员清单')

        // 设置列宽
        const columnWidths = [
          { wch: 12 }, // 姓名
          { wch: 15 }, // 手机号
          { wch: 20 }, // 身份证号
          { wch: 8 },  // 性别
          { wch: 8 },  // 年龄
          { wch: 15 }, // 可用推荐名额
          { wch: 12 }, // 黑名单状态
          { wch: 20 }  // 创建时间
        ]
        worksheet['!cols'] = columnWidths

        // 生成文件名（包含筛选条件和日期）
        const now = new Date()
        const dateStr = now.toISOString().split('T')[0]
        const keywordStr = this.filters.keyword ? `_${this.filters.keyword}` : ''
        const fileName = `学员清单_${dateStr}${keywordStr}.xlsx`

        // 导出文件
        XLSX.writeFile(workbook, fileName)
        
        alert(`成功导出 ${allStudents.length} 条学员记录`)
      } catch (error) {
        console.error('导出Excel失败', error)
        alert('导出失败，请重试')
      } finally {
        this.exporting = false
      }
    },
    async toggleDecrypt(entityId, field, entityType = 'STUDENT_PROFILE') {
      const key = `${entityType}_${entityId}_${field}`
      if (this.decryptedValues[key]) {
        // Toggle off - remove decrypted value
        delete this.decryptedValues[key]
        return
      }
      try {
        const value = await sensitiveDataService.decryptField(entityType, entityId, field)
        if (value !== undefined && value !== null) {
          this.decryptedValues[key] = value
        }
      } catch (error) {
        console.error('解密失败:', error)
        this.$message ? this.$message.error('解密失败：' + (error.message || '未知错误')) : alert('解密失败：' + (error.message || '未知错误'))
      }
    },
    getDisplayValue(entityId, field, maskedValue, entityType = 'STUDENT_PROFILE') {
      const key = `${entityType}_${entityId}_${field}`
      return this.decryptedValues[key] || maskedValue
    },
    async unbindWechat() {
      if (!this.profile?.id) return
      const confirmed = window.confirm('确定要解绑该学员的微信账号吗？解绑后学员需使用新微信号重新登录并完成报名，档案手机号将更新为新绑定手机号。')
      if (!confirmed) return
      try {
        this.loading = true
        const response = await studentService.unbindWechat(this.profile.id)
        if (response.success) {
          this.profile = response.data
          this.loadStudents()
          alert('解绑成功')
        }
      } catch (error) {
        console.error('解绑微信账号失败', error)
        const errorMsg = error?.error || error?.message || '解绑失败，请重试'
        alert(errorMsg)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.student-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}

.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}

.filter-item input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
  text-align: left;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-tag.normal {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.status-tag.danger {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.primary-button,
.ghost-button,
.success-button,
.link-button,
.danger-link {
  cursor: pointer;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
}

.success-button {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.25);
}

.success-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.ghost-button.small {
  padding: 0.35rem 0.8rem;
  font-size: 0.85rem;
}

.link-button {
  background: transparent;
  color: #2563eb;
  padding: 0.25rem 0.5rem;
}

.danger-link {
  background: transparent;
  color: #ef4444;
  padding: 0.35rem 0.8rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 1rem;
}

.modal {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  resize: vertical;
  min-height: 120px;
  box-sizing: border-box;
}

.modal-tip {
  color: #4b5563;
}

.form-summary {
  background: #f3f4f6;
  padding: 0.75rem;
  border-radius: 8px;
  color: #4b5563;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.form-summary p {
  margin: 0.25rem 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group span {
  font-size: 0.9rem;
  color: #374151;
  font-weight: 500;
}

.form-group textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  font-family: inherit;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 360px;
  height: 100%;
  background: #fff;
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.12);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 80;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-body {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.drawer-body section {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 8px;
  color: #4b5563;
  text-align: left;
}

.history-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

.muted {
  color: #9ca3af;
}

.close-button {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
}

.quota-link {
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
}

.quota-link:hover {
  color: #1d4ed8;
}

.quota-detail-modal {
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #111827;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.empty-state-small {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.quota-records-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quota-record-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.record-date {
  font-weight: 600;
  color: #374151;
}

.record-status {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.record-status.available {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.record-status.used {
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.record-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.record-content p {
  margin: 0;
}

.record-content strong {
  color: #111827;
}

.wechat-info {
  margin: 0.5rem 0;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.danger-button {
  background: #ef4444;
  color: #fff;
  padding: 0.5rem 0.9rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.danger-button:hover {
  background: #dc2626;
}

.decrypt-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.15rem 0.3rem;
  margin-left: 0.25rem;
  color: #6b7280;
  vertical-align: middle;
  border-radius: 4px;
  transition: color 0.2s ease, background 0.2s ease;
}

.decrypt-btn:hover {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
}

.eye-icon {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}

@media (max-width: 900px) {
  .drawer {
    width: 100%;
  }
}
</style>

